import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import ConfirmModal from '../components/ConfirmModal';
import { getPatients, createPatient, updatePatient, deletePatient, getPatientVitalFields, PatientVitalFields } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import PlaceSearch from '../components/PlaceSearch';
import GoogleMap from '../components/GoogleMap';
import { formatDateDDMMYYYY, getMaxDateToday } from '../utils/dateUtils';

interface Patient {
  _id: string;
  metadata: {
    name: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
    tenantId?: string | null;
  };
  spec: {
    age: number;
    gender?: string;
    phone?: string;
    email?: string;
    address?: string;
    dateOfBirth?: number;
    bloodGroup?: string;
    idNumber?: string;
    contactPerson?: {
      name?: string;
      phone?: string;
      relationship?: string;
    };
    medicalHistory?: string;
    allergies?: string;
    notes?: string;
    patientLocation?: {
      latitude: number;
      longitude: number;
      address?: string;
      details?: string;
    };
    vitalInfo?: Array<{
      fieldLabel: string;
      fieldType: 'TEXT' | 'NUMBER';
      value: string | number;
    }> | Record<string, any>; // Support both array format (new) and object format (legacy)
  };
}

// Format patient age - show days if less than 1 year
const formatPatientAge = (patient: Patient): string => {
  if (!patient) return 'N/A';

  const age = patient.spec?.age;
  if (age === undefined || age === null) return 'N/A';

  // If age is 0 or less than 1 year, calculate days from dateOfBirth
  if (age === 0 || age < 1) {
    const dateOfBirth = patient.spec?.dateOfBirth;
    if (dateOfBirth) {
      const dob = new Date(dateOfBirth);
      const today = new Date();
      const diffTime = today.getTime() - dob.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) return 'N/A'; // Future date
      if (diffDays === 0) return '0 days';
      if (diffDays === 1) return '1 day';
      return `${diffDays} days`;
    }
  }

  // For age >= 1, show years
  if (age === 1) return '1 year';
  return `${age} years`;
};

const PatientManagement: React.FC = () => {
  const { hasPermission, user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [vitalFields, setVitalFields] = useState<PatientVitalFields | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState<{ id: string; name: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [viewingPatient, setViewingPatient] = useState<Patient | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: '',
    phone: '',
    email: '',
    addressDetails: '',
    dateOfBirth: '',
    bloodGroup: '',
    idNumber: '',
    contactPersonName: '',
    contactPersonPhone: '',
    contactPersonRelationship: '',
    medicalHistory: '',
    allergies: '',
    notes: '',
    patientLocation: null as { latitude: number; longitude: number } | null,
    patientLocationAddress: '',
    vitalInfo: {} as Record<string, string | number>,
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [validationErrors, setValidationErrors] = useState({
    name: '',
    gender: '',
    age: '',
    dateOfBirth: '',
    email: '',
    phone: '',
    contactPersonName: '',
    contactPersonPhone: '',
    patientLocation: '',
  });

  // Close drawer with Escape key
  useEffect(() => {
    if (!showModal && !showViewModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && typeof activeElement.blur === 'function') {
          activeElement.blur();
        }
        if (showModal) {
          setShowModal(false);
        }
        if (showViewModal) {
          setShowViewModal(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal, showViewModal]);

  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 28.6139, lng: 77.209 });

  // Compute DOB bounds based on age (if provided)
  const parsedAge = formData.age && !isNaN(Number(formData.age)) ? Number(formData.age) : null;
  const dobYear =
    parsedAge !== null && parsedAge >= 0 && parsedAge <= 150
      ? new Date().getFullYear() - parsedAge
      : null;
  const dobMin = dobYear ? new Date(dobYear, 0, 1).toISOString().split('T')[0] : undefined;
  const dobMax = dobYear ? new Date(dobYear, 11, 31).toISOString().split('T')[0] : undefined;

  // Keep dateOfBirth within the derived year when age changes
  useEffect(() => {
    if (!dobYear || !formData.dateOfBirth) return;
    const currentDob = new Date(formData.dateOfBirth);
    if (isNaN(currentDob.getTime())) return;
    if (currentDob.getFullYear() !== dobYear) {
      // Snap to mid-year of the derived DOB year
      const midYearDate = new Date(dobYear, 6, 2).toISOString().split('T')[0];
      setFormData((prev) => ({ ...prev, dateOfBirth: midYearDate }));
    }
  }, [dobYear, formData.dateOfBirth]);

  // Derive age from DOB if age is empty and DOB is provided
  useEffect(() => {
    if (formData.age || !formData.dateOfBirth) return;
    const dobDate = new Date(formData.dateOfBirth);
    if (isNaN(dobDate.getTime())) return;
    const today = new Date();
    let age = today.getFullYear() - dobDate.getFullYear();
    const m = today.getMonth() - dobDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) {
      age--;
    }
    if (age >= 0 && age <= 150) {
      setFormData((prev) => ({ ...prev, age: age.toString() }));
    }
  }, [formData.age, formData.dateOfBirth]);

  const handlePlaceSelect = (lat: number, lng: number, address: string) => {
    setFormData((prev) => ({
      ...prev,
      patientLocation: { latitude: lat, longitude: lng },
      patientLocationAddress: address,
    }));
    setMapCenter({ lat, lng });
  };

  const reverseGeocode = async (lat: number, lng: number): Promise<string | null> => {
    if (!window.google || !window.google.maps) return null;
    return new Promise((resolve) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          resolve(results[0].formatted_address || null);
        } else {
          resolve(null);
        }
      });
    });
  };

  const handleMapLocationChange = async (lat: number, lng: number) => {
    const address = (await reverseGeocode(lat, lng)) || formData.patientLocationAddress || '';
    setFormData((prev) => ({
      ...prev,
      patientLocation: { latitude: lat, longitude: lng },
      patientLocationAddress: address,
    }));
    setMapCenter({ lat, lng });
  };

  // Fetch vital fields on component mount
  useEffect(() => {
    const fetchVitalFields = async () => {
      try {
        const tenantId = user?.isSystem ? undefined : user?.tenantId;
        const response = await getPatientVitalFields(tenantId);
        setVitalFields(response.data.data);
      } catch (err) {
        console.error('Failed to load vital fields:', err);
        // Don't show error to user, just continue without vital fields
      }
    };
    fetchVitalFields();
  }, [user]);

  // Initial load - fetch patients immediately on mount
  useEffect(() => {
    fetchPatients();
    setIsInitialLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search term - wait 500ms after user stops typing
  useEffect(() => {
    if (isInitialLoad) return; // Skip debounce on initial load

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm, isInitialLoad]);

  // Fetch patients when debounced search term changes (after initial load)
  useEffect(() => {
    if (isInitialLoad) return; // Skip on initial load (already fetched)
    fetchPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const fetchPatients = async (pageOverride?: number, pageSizeOverride?: number) => {
    try {
      setLoading(true);
      const pageToUse = pageOverride ?? currentPage;
      const sizeToUse = pageSizeOverride ?? pageSize;
      const params: any = { page: pageToUse, pageSize: sizeToUse };
      if (debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }
      const response = await getPatients(params.page, params.pageSize, params.search);
      setPatients(response.data.data || []);
      const pagination = response.data.pagination;
      if (pagination) {
        setCurrentPage(pagination.currentPage || pageToUse);
        setPageSize(pagination.pageSize || sizeToUse);
        setTotalItems(pagination.totalItems || 0);
        setHasNextPage(pagination.hasNextPage || false);
        setHasPreviousPage(pagination.hasPreviousPage || false);
      }
    } catch (err: any) {
      setError('Failed to load patients');
      console.error('Failed to load patients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (page: number) => {
    fetchPatients(page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    fetchPatients(1, size);
  };

  const handleRefresh = () => {
    fetchPatients();
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  const handleOpenViewModal = (patient: Patient) => {
    setViewingPatient(patient);
    setShowViewModal(true);
  };

  const handleOpenModal = (patient?: Patient) => {
    if (patient) {
      setEditingPatient(patient);
      setFormData({
        name: patient.metadata.name,
        age: patient.spec.age ? patient.spec.age.toString() : '',
        gender: patient.spec.gender || '',
        phone: patient.spec.phone || '',
        email: patient.spec.email || '',
        addressDetails: patient.spec.address || '',
        dateOfBirth: patient.spec.dateOfBirth
          ? new Date(patient.spec.dateOfBirth).toISOString().split('T')[0]
          : '',
        bloodGroup: patient.spec.bloodGroup || '',
        idNumber: patient.spec.idNumber || '',
        contactPersonName: patient.spec.contactPerson?.name || '',
        contactPersonPhone: patient.spec.contactPerson?.phone || '',
        contactPersonRelationship: patient.spec.contactPerson?.relationship || '',
        medicalHistory: patient.spec.medicalHistory || '',
        allergies: patient.spec.allergies || '',
        notes: patient.spec.notes || '',
        patientLocation: patient.spec.patientLocation
          ? {
            latitude: patient.spec.patientLocation.latitude,
            longitude: patient.spec.patientLocation.longitude,
          }
          : null,
        patientLocationAddress: '',
        vitalInfo: Array.isArray(patient.spec.vitalInfo)
          ? (() => {
            // Convert array format to key-value format for form
            const result: Record<string, any> = {};
            if (vitalFields?.spec.fields) {
              patient.spec.vitalInfo.forEach((item: any) => {
                // Find field by matching fieldLabel and fieldType
                const field = vitalFields.spec.fields.find(
                  (f: any) => f.fieldLabel === item.fieldLabel && f.fieldType === item.fieldType && !f.isDeleted
                );
                if (field?.fieldKey) {
                  result[field.fieldKey] = item.value;
                }
              });
            }
            return result;
          })()
          : (patient.spec.vitalInfo || {}),
      });
      if (patient.spec.patientLocation) {
        setMapCenter({
          lat: patient.spec.patientLocation.latitude,
          lng: patient.spec.patientLocation.longitude,
        });
      }
    } else {
      setEditingPatient(null);
      setFormData({
        name: '',
        age: '',
        gender: '',
        phone: '',
        email: '',
        addressDetails: '',
        dateOfBirth: '',
        bloodGroup: '',
        idNumber: '',
        contactPersonName: '',
        contactPersonPhone: '',
        contactPersonRelationship: '',
        medicalHistory: '',
        allergies: '',
        notes: '',
        patientLocation: null,
        patientLocationAddress: '',
        vitalInfo: {},
      });
      setMapCenter({ lat: 28.6139, lng: 77.209 });
    }
    setShowModal(true);
    setError('');
    setValidationErrors({
      name: '',
      gender: '',
      age: '',
      dateOfBirth: '',
      email: '',
      phone: '',
      contactPersonName: '',
      contactPersonPhone: '',
      patientLocation: '',
    });
  };

  const validateEmail = (email: string): string => {
    if (!email) return '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Invalid email format';
    }
    return '';
  };

  const validatePhone = (phone: string, isRequired: boolean = true): string => {
    if (!phone || !phone.trim()) {
      return isRequired ? 'Phone number is required' : '';
    }
    // Remove spaces, dashes, and parentheses for validation
    const cleanedPhone = phone.replace(/[\s\-()]/g, '');
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(cleanedPhone)) {
      return 'Phone number must be exactly 10 digits';
    }
    return '';
  };

  // Capitalize the first letter of each word in a name
  const capitalizeName = (value: string): string => {
    if (!value || typeof value !== 'string') return value;
    return value
      .trim()
      .split(/\s+/)
      .map((word) => {
        if (!word) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  const handleFieldChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });

    // Validate on change
    if (field === 'email') {
      setValidationErrors({ ...validationErrors, email: validateEmail(value) });
    } else if (field === 'phone') {
      setValidationErrors({ ...validationErrors, phone: validatePhone(value, false) });
    } else if (field === 'contactPersonName') {
      setValidationErrors({ ...validationErrors, contactPersonName: '' });
    } else if (field === 'contactPersonPhone') {
      setValidationErrors({ ...validationErrors, contactPersonPhone: validatePhone(value, false) });
    } else if (field === 'name') {
      setValidationErrors({ ...validationErrors, name: '' });
    } else if (field === 'gender') {
      setValidationErrors({ ...validationErrors, gender: '' });
    } else if (field === 'age') {
      setValidationErrors({ ...validationErrors, age: '' }); // handled on submit with DOB rule
    } else if (field === 'dateOfBirth') {
      setValidationErrors({ ...validationErrors, dateOfBirth: '' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);

    // Only patient location is required
    const patientLocationError = formData.patientLocation ? '' : 'Patient location is required';

    // Validate age if provided
    let ageError = '';
    if (formData.age !== '' && !isNaN(Number(formData.age))) {
      const ageNum = Number(formData.age);
      if (ageNum < 0 || ageNum > 150) {
        ageError = 'Age must be between 0 and 150';
      }
    }

    // Validate email if provided
    let emailError = '';
    if (formData.email && formData.email.trim()) {
      emailError = validateEmail(formData.email);
    }

    // Validate phone if provided
    let phoneError = '';
    if (formData.phone && formData.phone.trim()) {
      phoneError = validatePhone(formData.phone, false);
    }

    // Validate contact person - phone number is mandatory
    let contactPersonNameError = '';
    let contactPersonPhoneError = '';
    const hasContactPersonPhone = formData.contactPersonPhone && formData.contactPersonPhone.trim();

    if (!hasContactPersonPhone) {
      contactPersonPhoneError = 'Contact person phone number is required';
    } else {
      // Validate phone format
      contactPersonPhoneError = validatePhone(formData.contactPersonPhone, false);
    }

    // Collect and set all validation errors
    const nextValidationErrors = {
      ...validationErrors,
      name: '',
      age: ageError,
      dateOfBirth: '',
      email: emailError,
      phone: phoneError,
      contactPersonName: contactPersonNameError,
      contactPersonPhone: contactPersonPhoneError,
      patientLocation: patientLocationError,
    };

    setValidationErrors(nextValidationErrors);

    // Check if there are any validation errors
    if (
      ageError ||
      emailError ||
      phoneError ||
      contactPersonNameError ||
      contactPersonPhoneError ||
      patientLocationError
    ) {
      setError('Please fix the highlighted errors.');
      setSubmitting(false);
      return;
    }

    try {
      const data: any = {
        metadata: { name: capitalizeName(formData.name.trim()) },
        spec: {
          age: Number(formData.age),
          gender: formData.gender || undefined,
          phone: formData.phone.trim(),
          email: formData.email.trim() || undefined,
          address: (() => {
            const details = formData.addressDetails.trim();
            const geo = formData.patientLocationAddress.trim();
            const parts = [details, geo].filter((p) => p);
            return parts.length ? parts.join(' | ') : undefined;
          })(),
          dateOfBirth: formData.dateOfBirth ? new Date(formData.dateOfBirth).getTime() : undefined,
          bloodGroup: formData.bloodGroup || undefined,
          idNumber: formData.idNumber.trim() || undefined,
          contactPerson: (formData.contactPersonName.trim() || formData.contactPersonPhone.trim()) ? {
            name: formData.contactPersonName.trim() ? capitalizeName(formData.contactPersonName.trim()) : undefined,
            phone: formData.contactPersonPhone.trim() || undefined,
            relationship: formData.contactPersonRelationship.trim() || undefined,
          } : undefined,
          medicalHistory: formData.medicalHistory.trim() || undefined,
          allergies: formData.allergies.trim() || undefined,
          notes: formData.notes.trim() || undefined,
          patientLocation: formData.patientLocation
            ? {
              latitude: formData.patientLocation.latitude,
              longitude: formData.patientLocation.longitude,
            }
            : undefined,
          vitalInfo: Object.keys(formData.vitalInfo).length > 0 ? formData.vitalInfo : undefined,
        },
      };

      if (editingPatient) {
        await updatePatient(editingPatient._id, data);
      } else {
        await createPatient(data);
      }

      setShowModal(false);
      fetchPatients();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    setPatientToDelete({ id, name });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!patientToDelete) return;

    try {
      await deletePatient(patientToDelete.id);
      fetchPatients();
      setShowDeleteConfirm(false);
      setPatientToDelete(null);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to delete patient. Unknown error occurred.');
      setShowDeleteConfirm(false);
      setPatientToDelete(null);
      setShowErrorModal(true);
    }
  };

  return (
    <DashboardLayout>
      {/* <div className="page-header">
        <h2>Patient Management</h2>
        <p>Manage patient records and information</p>
      </div> */}

      <div className="card" style={{ marginTop: '24px', height: "calc(100vh - 185px)" }}>
        <div className="card-header">
          {/* <h3 className="card-title">Patients</h3> */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search by name, phone, or email..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  width: '220px',
                  flex: '0 0 220px',
                }}
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  style={{
                    padding: '8px 16px',
                    background: '#999',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px',
                  }}
                >
                  Clear
                </button>
              )}
              {debouncedSearchTerm && debouncedSearchTerm !== searchTerm && (
                <span style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                  Searching...
                </span>
              )}
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button"
              title="Refresh list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            {hasPermission('patient.create') && (
              <button className="btn-add" onClick={() => handleOpenModal()}>
                + Add Patient
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading patients...</div>
        ) : patients.length === 0 ? (
          <div className="empty-state">
            <h3>No patients yet</h3>
            <p>Create your first patient record to get started</p>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div className="table-container" style={{ height: 'calc(100vh - 340px)', overflow: 'auto' }}>
              <table className="bookings-table">
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th>Name</th>
                    <th>Age</th>
                    <th>Gender</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Contact Person</th>
                    <th>Contact Phone</th>
                    <th>Blood Group</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {patients.map((patient) => (
                    <tr
                      key={patient._id}
                      onClick={() => handleOpenViewModal(patient)}
                      style={{
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <td>
                        <strong>{patient.metadata.name}</strong>
                      </td>
                      <td>{formatPatientAge(patient)}</td>
                      <td>
                        {patient.spec.gender ? (
                          <span style={{ textTransform: 'capitalize' }}>{patient.spec.gender}</span>
                        ) : (
                          <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>
                        )}
                      </td>
                      <td>{patient.spec.phone || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}</td>
                      <td>{patient.spec.email || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}</td>
                      <td>{patient.spec.contactPerson?.name || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}</td>
                      <td>{patient.spec.contactPerson?.phone || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}</td>
                      <td>{patient.spec.bloodGroup || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}</td>
                      <td>{formatDateDDMMYYYY(patient.metadata.createdAt)}</td>
                      <td>
                        <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                          <button className="btn-icon btn-view" onClick={() => handleOpenViewModal(patient)}>
                            View
                          </button>
                          {hasPermission('patient.update') && (
                            <button className="btn-icon btn-edit" onClick={() => handleOpenModal(patient)}>
                              Edit
                            </button>
                          )}
                          {hasPermission('patient.delete') && (
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDelete(patient._id, patient.metadata.name)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination-controls" style={{ position: 'relative', width: '100%' }}>
              <div className="pagination-left" style={{ position: 'absolute', right: 250, whiteSpace: 'nowrap', zIndex: 10 }}>
                <label style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                  Rows per page:{' '}
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    style={{ minWidth: '60px', width: 'auto' }}
                  >
                    {[10, 25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="pagination-center">
                <span className="pagination-info">
                  {totalItems === 0
                    ? '0 of 0'
                    : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                      currentPage * pageSize,
                      totalItems
                    )} of ${totalItems}`}
                </span>
              </div>
              <div className="pagination-right">
                <button
                  className="btn-secondary"
                  disabled={!hasPreviousPage || loading}
                  onClick={() => hasPreviousPage && handlePageChange(currentPage - 1)}
                >
                  ‹
                </button>
                <button
                  className="btn-secondary"
                  disabled={!hasNextPage || loading}
                  onClick={() => hasNextPage && handlePageChange(currentPage + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="drawer-overlay" onClick={() => setShowModal(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">{editingPatient ? 'Edit Patient' : 'Create New Patient'}</h3>
              <button className="drawer-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="drawer-form">
              <div className="drawer-body">
                {error && <div className="error-message">{error}</div>}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label>
                      Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      placeholder="Full name"
                      className={validationErrors.name ? 'input-error' : ''}
                    />
                    {validationErrors.name && (
                      <small style={{ color: '#dc3545', fontSize: '14px', display: 'block', marginTop: '4px' }}>
                        {validationErrors.name}
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Age</label>
                    <input
                      type="number"
                      value={formData.age}
                      onChange={(e) => handleFieldChange('age', e.target.value)}
                      min="0"
                      max="150"
                      placeholder="Age"
                      className={validationErrors.age ? 'input-error' : ''}
                    />
                    {validationErrors.age && (
                      <small style={{ color: '#dc3545', fontSize: '14px', display: 'block', marginTop: '4px' }}>
                        {validationErrors.age}
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>
                      Gender
                    </label>
                    <select
                      value={formData.gender}
                      onChange={(e) => handleFieldChange('gender', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: isDark ? '1px solid #475569' : '1px solid #ddd',
                        borderRadius: '6px',
                        backgroundColor: isDark ? '#1e293b' : 'white',
                        color: isDark ? '#f1f5f9' : '#000'
                      }}
                      className={validationErrors.gender ? 'input-error' : ''}
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                    {validationErrors.gender && (
                      <small style={{ color: '#dc3545', fontSize: '14px', display: 'block', marginTop: '4px' }}>
                        {validationErrors.gender}
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) => handleFieldChange('dateOfBirth', e.target.value)}
                      min={dobMin}
                      max={dobMax && dobMax < getMaxDateToday() ? dobMax : getMaxDateToday()}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: isDark ? '1px solid #475569' : '1px solid #ddd',
                        borderRadius: '6px',
                        backgroundColor: isDark ? '#1e293b' : 'white',
                        color: isDark ? '#f1f5f9' : '#000'
                      }}
                      className={validationErrors.dateOfBirth ? 'input-error' : ''}
                    />
                    {dobYear !== null && (
                      <small style={{ color: '#666' }}>
                        Select a date in {dobYear} (based on age)
                      </small>
                    )}
                    {validationErrors.dateOfBirth && (
                      <small style={{ color: '#dc3545', fontSize: '14px', display: 'block', marginTop: '4px' }}>
                        {validationErrors.dateOfBirth}
                      </small>
                    )}
                    {/* {!validationErrors.age && !validationErrors.dateOfBirth && (
                      <small style={{ color: '#666', fontSize: '14px', display: 'block', marginTop: '4px' }}>
                        Provide either age or date of birth (or both)
                      </small>
                    )} */}
                  </div>

                  <div className="form-group">
                    <label>
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleFieldChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="Phone number (10 digits)"
                      className={validationErrors.phone ? 'input-error' : ''}
                      maxLength={10}
                    />
                    {validationErrors.phone && (
                      <small style={{ color: '#dc3545', fontSize: '14px', display: 'block', marginTop: '4px' }}>
                        {validationErrors.phone}
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>ID Number</label>
                    <input
                      type="text"
                      value={formData.idNumber}
                      onChange={(e) => setFormData({ ...formData, idNumber: e.target.value })}
                      placeholder="Aadhar, PAN, Student ID, etc."
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: isDark ? '1px solid #475569' : '1px solid #ddd',
                        borderRadius: '6px',
                        backgroundColor: isDark ? '#1e293b' : 'white',
                        color: isDark ? '#f1f5f9' : '#000'
                      }}
                    />
                    <small style={{ color: isDark ? '#94a3b8' : '#666', fontSize: '14px', display: 'block', marginTop: '4px' }}>
                      Enter ID number (Aadhar, PAN, Student ID, etc.)
                    </small>
                  </div>

                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>
                      Patient Location (select on map) <span className="required-star">*</span>
                    </label>
                    <PlaceSearch
                      onPlaceSelect={handlePlaceSelect}
                      placeholder="Search for a place..."
                      currentLocation={
                        formData.patientLocation
                          ? {
                            latitude: formData.patientLocation.latitude,
                            longitude: formData.patientLocation.longitude,
                            address: formData.patientLocationAddress,
                          }
                          : undefined
                      }
                    />
                    <div style={{
                      marginTop: '8px',
                      border: validationErrors.patientLocation ? '2px solid #dc3545' : 'none',
                      borderRadius: validationErrors.patientLocation ? '6px' : '0',
                      padding: validationErrors.patientLocation ? '2px' : '0'
                    }}>
                      <GoogleMap
                        center={mapCenter}
                        location={
                          formData.patientLocation
                            ? {
                              latitude: formData.patientLocation.latitude,
                              longitude: formData.patientLocation.longitude,
                              address: formData.patientLocationAddress,
                            }
                            : null
                        }
                        onLocationChange={handleMapLocationChange}
                        height="280px"
                      />
                    </div>
                    {formData.patientLocationAddress && (
                      <div style={{ marginTop: '8px', color: '#555' }}>
                        <strong>Detected address:</strong> {formData.patientLocationAddress}
                      </div>
                    )}
                    <div style={{ marginTop: '8px' }}>
                      <label>Address details (flat/area/landmark)</label>
                      <textarea
                        value={formData.addressDetails}
                        onChange={(e) => setFormData({ ...formData, addressDetails: e.target.value })}
                        placeholder="e.g., Flat 203, Block B, near City Mall"
                        rows={2}
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: isDark ? '1px solid #475569' : '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '16px',
                          fontFamily: 'inherit',
                          resize: 'vertical',
                          backgroundColor: isDark ? '#1e293b' : 'white',
                          color: isDark ? '#f1f5f9' : '#000'
                        }}
                      />
                    </div>
                    {validationErrors.patientLocation && (
                      <small style={{ color: '#dc3545', fontSize: '14px', display: 'block', marginTop: '4px' }}>
                        {validationErrors.patientLocation}
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Blood Group</label>
                    <select
                      value={formData.bloodGroup}
                      onChange={(e) => setFormData({ ...formData, bloodGroup: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: isDark ? '1px solid #475569' : '1px solid #ddd',
                        borderRadius: '6px',
                        backgroundColor: isDark ? '#1e293b' : 'white',
                        color: isDark ? '#f1f5f9' : '#000'
                      }}
                    >
                      <option value="">Select blood group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleFieldChange('email', e.target.value)}
                      placeholder="Email address"
                      style={{
                        borderColor: validationErrors.email ? '#dc3545' : undefined,
                      }}
                    />
                    {validationErrors.email && (
                      <small style={{ color: '#dc3545', fontSize: '14px', display: 'block', marginTop: '4px' }}>
                        {validationErrors.email}
                      </small>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: '16px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
                  <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>Contact Person</h4>
                  {validationErrors.contactPersonName && (
                    <div style={{
                      marginBottom: '12px',
                      padding: '8px 12px',
                      backgroundColor: isDark ? '#7f1d1d' : '#fee',
                      border: isDark ? '1px solid #991b1b' : '1px solid #fcc',
                      borderRadius: '4px'
                    }}>
                      <small style={{ color: isDark ? '#fca5a5' : '#dc3545', fontSize: '14px' }}>
                        {validationErrors.contactPersonName}
                      </small>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>
                        Contact Person Name
                      </label>
                      <input
                        type="text"
                        value={formData.contactPersonName}
                        onChange={(e) => handleFieldChange('contactPersonName', e.target.value)}
                        placeholder="Contact person name"
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Contact Person Number <span className="required-star">*</span>
                      </label>
                      <input
                        type="tel"
                        value={formData.contactPersonPhone}
                        onChange={(e) =>
                          handleFieldChange('contactPersonPhone', e.target.value.replace(/\D/g, '').slice(0, 10))
                        }
                        placeholder="Contact person number (10 digits)"
                        className={validationErrors.contactPersonPhone ? 'input-error' : ''}
                        maxLength={10}
                      />
                      {validationErrors.contactPersonPhone && (
                        <small style={{ color: '#dc3545', fontSize: '14px', display: 'block', marginTop: '4px' }}>
                          {validationErrors.contactPersonPhone}
                        </small>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Relationship</label>
                      <input
                        type="text"
                        value={formData.contactPersonRelationship}
                        onChange={(e) => setFormData({ ...formData, contactPersonRelationship: e.target.value })}
                        placeholder="e.g., Spouse, Parent"
                      />
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '16px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
                  <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>Medical Information</h4>
                  <div className="form-group">
                    <label>Medical History</label>
                    <textarea
                      value={formData.medicalHistory}
                      onChange={(e) => setFormData({ ...formData, medicalHistory: e.target.value })}
                      placeholder="Previous medical conditions, surgeries, etc."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: isDark ? '1px solid #475569' : '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        backgroundColor: isDark ? '#1e293b' : 'white',
                        color: isDark ? '#f1f5f9' : '#000'
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Allergies</label>
                    <textarea
                      value={formData.allergies}
                      onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                      placeholder="Known allergies"
                      rows={2}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: isDark ? '1px solid #475569' : '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        backgroundColor: isDark ? '#1e293b' : 'white',
                        color: isDark ? '#f1f5f9' : '#000'
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Additional notes"
                      rows={2}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: isDark ? '1px solid #475569' : '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        backgroundColor: isDark ? '#1e293b' : 'white',
                        color: isDark ? '#f1f5f9' : '#000'
                      }}
                    />
                  </div>

                  {/* Vital Information Fields */}
                  {vitalFields && vitalFields.spec.fields.length > 0 && (
                    <>
                      <div style={{
                        marginTop: '24px',
                        marginBottom: '16px',
                        paddingTop: '24px',
                        borderTop: isDark ? '1px solid #475569' : '1px solid #e2e8f0'
                      }}>
                        <h4 style={{
                          marginBottom: '16px',
                          fontSize: '18px',
                          fontWeight: '600',
                          color: isDark ? '#f1f5f9' : '#1e293b'
                        }}>
                          Vital Information
                        </h4>
                        {vitalFields.spec.fields.map((field, index) => (
                          <div key={index} className="form-group" style={{ marginBottom: '16px' }}>
                            <label>{field.fieldLabel}</label>
                            {field.fieldType === 'NUMBER' ? (
                              <input
                                type="number"
                                value={formData.vitalInfo[field.fieldKey] || ''}
                                onChange={(e) => {
                                  const value = e.target.value === '' ? '' : Number(e.target.value);
                                  setFormData({
                                    ...formData,
                                    vitalInfo: {
                                      ...formData.vitalInfo,
                                      [field.fieldKey]: value,
                                    },
                                  });
                                }}
                                placeholder={`Enter ${field.fieldLabel.toLowerCase()}`}
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  border: isDark ? '1px solid #475569' : '1px solid #ddd',
                                  borderRadius: '6px',
                                  fontSize: '16px',
                                  backgroundColor: isDark ? '#1e293b' : 'white',
                                  color: isDark ? '#f1f5f9' : '#000'
                                }}
                              />
                            ) : (
                              <input
                                type="text"
                                value={formData.vitalInfo[field.fieldKey] || ''}
                                onChange={(e) => {
                                  setFormData({
                                    ...formData,
                                    vitalInfo: {
                                      ...formData.vitalInfo,
                                      [field.fieldKey]: e.target.value,
                                    },
                                  });
                                }}
                                placeholder={`Enter ${field.fieldLabel.toLowerCase()}`}
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  border: isDark ? '1px solid #475569' : '1px solid #ddd',
                                  borderRadius: '6px',
                                  fontSize: '16px',
                                  backgroundColor: isDark ? '#1e293b' : 'white',
                                  color: isDark ? '#f1f5f9' : '#000'
                                }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="drawer-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <span className="loading-spinner btn-spinner" aria-hidden />
                      {editingPatient ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    `${editingPatient ? 'Update' : 'Create'} Patient`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showViewModal && viewingPatient && (
        <div className="drawer-overlay" onClick={() => setShowViewModal(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">View Patient Details</h3>
              <button className="drawer-close" onClick={() => setShowViewModal(false)}>
                ×
              </button>
            </div>

            <div className="drawer-body" style={{ padding: '24px', overflowY: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Name</label>
                  <div style={{
                    padding: '10px',
                    border: isDark ? '1px solid #475569' : '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                    color: isDark ? '#f1f5f9' : '#000',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {viewingPatient.metadata.name || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Age</label>
                  <div style={{
                    padding: '10px',
                    border: isDark ? '1px solid #475569' : '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                    color: isDark ? '#f1f5f9' : '#000',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {formatPatientAge(viewingPatient)}
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Gender</label>
                  <div style={{
                    padding: '10px',
                    border: isDark ? '1px solid #475569' : '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                    color: isDark ? '#f1f5f9' : '#000',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    textTransform: 'capitalize'
                  }}>
                    {viewingPatient.spec.gender || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Date of Birth</label>
                  <div style={{
                    padding: '10px',
                    border: isDark ? '1px solid #475569' : '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                    color: isDark ? '#f1f5f9' : '#000',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {viewingPatient.spec.dateOfBirth
                      ? formatDateDDMMYYYY(viewingPatient.spec.dateOfBirth)
                      : <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Phone</label>
                  <div style={{
                    padding: '10px',
                    border: isDark ? '1px solid #475569' : '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                    color: isDark ? '#f1f5f9' : '#000',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {viewingPatient.spec.phone || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Email</label>
                  <div style={{
                    padding: '10px',
                    border: isDark ? '1px solid #475569' : '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                    color: isDark ? '#f1f5f9' : '#000',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {viewingPatient.spec.email || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Blood Group</label>
                  <div style={{
                    padding: '10px',
                    border: isDark ? '1px solid #475569' : '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                    color: isDark ? '#f1f5f9' : '#000',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {viewingPatient.spec.bloodGroup || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>ID Number</label>
                  <div style={{
                    padding: '10px',
                    border: isDark ? '1px solid #475569' : '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                    color: isDark ? '#f1f5f9' : '#000',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {viewingPatient.spec.idNumber || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                  </div>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '16px' }}>
                <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Address</label>
                <div style={{
                  padding: '10px',
                  border: isDark ? '1px solid #475569' : '1px solid #ddd',
                  borderRadius: '6px',
                  backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                  color: isDark ? '#f1f5f9' : '#000',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {viewingPatient.spec.address || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                </div>
              </div>

              {viewingPatient.spec.patientLocation && (
                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Location</label>
                  <div style={{
                    padding: '10px',
                    border: isDark ? '1px solid #475569' : '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                    color: isDark ? '#f1f5f9' : '#000',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {viewingPatient.spec.patientLocation.latitude.toFixed(6)}, {viewingPatient.spec.patientLocation.longitude.toFixed(6)}
                  </div>
                </div>
              )}

              <div style={{ marginTop: '16px', borderTop: isDark ? '1px solid #475569' : '1px solid #eee', paddingTop: '16px' }}>
                <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>Contact Person</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Name</label>
                    <div style={{
                      padding: '10px',
                      border: isDark ? '1px solid #475569' : '1px solid #ddd',
                      borderRadius: '6px',
                      backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                      color: isDark ? '#f1f5f9' : '#000',
                      minHeight: '40px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {viewingPatient.spec.contactPerson?.name || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Phone</label>
                    <div style={{
                      padding: '10px',
                      border: isDark ? '1px solid #475569' : '1px solid #ddd',
                      borderRadius: '6px',
                      backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                      color: isDark ? '#f1f5f9' : '#000',
                      minHeight: '40px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {viewingPatient.spec.contactPerson?.phone || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Relationship</label>
                    <div style={{
                      padding: '10px',
                      border: isDark ? '1px solid #475569' : '1px solid #ddd',
                      borderRadius: '6px',
                      backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                      color: isDark ? '#f1f5f9' : '#000',
                      minHeight: '40px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {viewingPatient.spec.contactPerson?.relationship || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '16px', borderTop: isDark ? '1px solid #475569' : '1px solid #eee', paddingTop: '16px' }}>
                <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: '600' }}>Medical Information</h4>
                <div className="form-group">
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Medical History</label>
                  <div style={{
                    padding: '10px',
                    border: isDark ? '1px solid #475569' : '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                    color: isDark ? '#f1f5f9' : '#000',
                    minHeight: '60px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {viewingPatient.spec.medicalHistory || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                  </div>
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Allergies</label>
                  <div style={{
                    padding: '10px',
                    border: isDark ? '1px solid #475569' : '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                    color: isDark ? '#f1f5f9' : '#000',
                    minHeight: '60px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {viewingPatient.spec.allergies || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                  </div>
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Notes</label>
                  <div style={{
                    padding: '10px',
                    border: isDark ? '1px solid #475569' : '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                    color: isDark ? '#f1f5f9' : '#000',
                    minHeight: '60px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {viewingPatient.spec.notes || <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                  </div>
                </div>
              </div>

              {/* Vital Information */}
              {vitalFields && vitalFields.spec.fields.length > 0 && (
                <div style={{
                  marginTop: '24px',
                  marginBottom: '16px',
                  paddingTop: '24px',
                  borderTop: isDark ? '1px solid #475569' : '1px solid #e2e8f0'
                }}>
                  <h4 style={{
                    marginBottom: '16px',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: isDark ? '#f1f5f9' : '#1e293b'
                  }}>
                    Vital Information
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    {vitalFields.spec.fields
                      .filter((field) => !field.isDeleted)
                      .map((field) => {
                        // Get value from patient's vitalInfo
                        let value: any = null;

                        if (viewingPatient.spec.vitalInfo) {
                          if (Array.isArray(viewingPatient.spec.vitalInfo)) {
                            // Array format: find by fieldKey or fieldLabel
                            const item = viewingPatient.spec.vitalInfo.find(
                              (item: any) =>
                                item.fieldKey === field.fieldKey ||
                                (item.fieldLabel === field.fieldLabel && item.fieldType === field.fieldType)
                            );
                            value = item?.value;
                          } else {
                            // Object format: get by fieldKey
                            value = (viewingPatient.spec.vitalInfo as Record<string, any>)[field.fieldKey];
                          }
                        }

                        return (
                          <div key={field.fieldKey} className="form-group">
                            <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                              {field.fieldLabel}
                            </label>
                            <div style={{
                              padding: '10px',
                              border: isDark ? '1px solid #475569' : '1px solid #ddd',
                              borderRadius: '6px',
                              backgroundColor: isDark ? '#1e293b' : '#f8f9fa',
                              color: isDark ? '#f1f5f9' : '#000',
                              minHeight: '40px',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              {value !== null && value !== undefined && value !== ''
                                ? String(value)
                                : <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            <div className="drawer-footer">
              <button type="button" className="btn-cancel" onClick={() => setShowViewModal(false)}>
                Close
              </button>
              {hasPermission('patient.update') && (
                <button
                  type="button"
                  className="btn-submit"
                  onClick={() => {
                    setShowViewModal(false);
                    handleOpenModal(viewingPatient);
                  }}
                >
                  Edit Patient
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Patient"
        message={`Are you sure you want to delete the patient "${patientToDelete?.name}"? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setPatientToDelete(null);
        }}
      />

      <ConfirmModal
        isOpen={showErrorModal}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText=""
        type="danger"
        onConfirm={() => setShowErrorModal(false)}
        onCancel={() => setShowErrorModal(false)}
      />
    </DashboardLayout>
  );
};

export default PatientManagement;
